import type { Event } from '~shared/types'
import { UserRole } from '~shared/types'
import { useDashboardPermission, useUser } from '../../user'
import { useTeam } from '../../team'
import { useEventsByTeamId } from '../api/hooks'
import { useTeamQueryEnabled } from '../../../shared/lib/use-query-guards'

type UseUserEventsParams = {
  teamId?: string
}

/**
 * Hook to get events that the current user has access to.
 *
 * - Team-level users (TEAM_VIEWER and above) can see all events
 * - Event-level users can only see events they're assigned to
 *
 * @param teamId - The team to fetch events for
 *
 * @returns Array of events the user can access
 */
export const useUserEvents = ({ teamId }: UseUserEventsParams): Event[] => {
  const { data: user } = useUser()
  const queryEnabled = useTeamQueryEnabled(teamId)

  const { data: team } = useTeam({
    params: {
      teamId: teamId as string,
    },
    options: {
      enabled: queryEnabled,
    },
  })

  const { data: events } = useEventsByTeamId({
    params: {
      teamId: teamId as string,
    },
    options: {
      enabled: queryEnabled,
    },
  })

  const isTeamLevelUser = useDashboardPermission({
    role: UserRole.TEAM_VIEWER,
  })

  return (
    events?.filter((event: Event) => {
      if (!user) return false
      if (isTeamLevelUser) return true

      const teamMember = team?.members?.[user.uid]
      if (!teamMember) return false

      return teamMember?.eventIds?.includes(event.id)
    }) ?? []
  )
}
